/**
 * RecentlyViewedProducts - Premium
 * Tracks recently viewed products via localStorage and renders Dawn-compatible product cards.
 * Features: Quick Add, vendor, rating, sale badges, slider, remove button.
 */

class RecentlyViewedProducts extends HTMLElement {
  constructor() {
    super();
    this.gridEl = this.querySelector('[data-recently-viewed-grid]');
    this.emptyEl = this.querySelector('[data-recently-viewed-empty]');
    this.currentHandle = this.dataset.productHandle;
    this.maxCount = parseInt(this.dataset.maxCount, 10) || 6;
    this.productsUrl = this.dataset.productsUrl;
    this.storageKey = 'recentlyViewedProducts';
    this.showVendor = this.dataset.showVendor === 'true';
    this.showPrice = this.dataset.showPrice !== 'false';
    this.showCompareAtPrice = this.dataset.showCompareAtPrice !== 'false';
    this.showSaleBadge = this.dataset.showSaleBadge !== 'false';
    this.showRating = this.dataset.showRating === 'true';
    this.showQuickAdd = this.dataset.showQuickAdd === 'standard';
    this.desktopColumns = parseInt(this.dataset.desktopColumns, 10) || 4;
    this.mobileColumns = parseInt(this.dataset.mobileColumns, 10) || 2;
    this.allProducts = [];
  }

  connectedCallback() {
    this.trackProduct();
    this.loadRecentlyViewed();
  }

  disconnectedCallback() {
    if (this._scrollHandler) {
      window.removeEventListener('scroll', this._scrollHandler);
    }
  }

  trackProduct() {
    if (!this.currentHandle) return;

    const stored = this.getStored();
    const filtered = stored.filter((h) => h !== this.currentHandle);
    filtered.unshift(this.currentHandle);
    const trimmed = filtered.slice(0, 15);
    this.setStored(trimmed);
  }

  getStored() {
    try {
      const raw = localStorage.getItem(this.storageKey);
      return raw ? JSON.parse(raw) : [];
    } catch {
      return [];
    }
  }

  setStored(handles) {
    try {
      localStorage.setItem(this.storageKey, JSON.stringify(handles));
    } catch {
      // Storage full or unavailable
    }
  }

  removeFromStored(handle) {
    const stored = this.getStored();
    const filtered = stored.filter((h) => h !== handle);
    this.setStored(filtered);
  }

  async loadRecentlyViewed() {
    let handles = this.getStored();

    if (this.currentHandle) {
      handles = handles.filter((h) => h !== this.currentHandle);
    }

    handles = handles.slice(0, this.maxCount);

    if (handles.length === 0) {
      this.showEmpty();
      return;
    }

    this.showLoading();

    const productPromises = handles.map((handle) =>
      fetch(`${this.productsUrl}products/${handle}.js`)
        .then((res) => (res.ok ? res.json() : null))
        .catch(() => null)
    );

    const products = await Promise.all(productPromises);
    this.allProducts = products.filter((p) => p !== null);

    if (this.allProducts.length === 0) {
      this.showEmpty();
      return;
    }

    this.hideEmpty();
    this.renderCards();
    this.initQuickAdd();
  }

  showEmpty() {
    if (this.gridEl) this.gridEl.style.display = 'none';
    if (this.emptyEl) this.emptyEl.style.display = 'block';
  }

  hideEmpty() {
    if (this.emptyEl) this.emptyEl.style.display = 'none';
    if (this.gridEl) this.gridEl.style.display = '';
  }

  showLoading() {
    if (!this.gridEl) return;
    let html = '';
    for (let i = 0; i < this.maxCount; i++) {
      html += `<div class="grid__item recently-viewed__card"><div class="recently-viewed__skeleton"></div></div>`;
    }
    this.gridEl.innerHTML = html;
  }

  renderCards() {
    if (!this.gridEl) return;

    this.gridEl.setAttribute('data-desktop-columns', this.desktopColumns);
    this.gridEl.setAttribute('data-mobile-columns', this.mobileColumns);

    const html = this.allProducts
      .map((product, index) => this.buildCard(product, index))
      .join('');

    this.gridEl.innerHTML = html;

    requestAnimationFrame(() => {
      const cards = this.gridEl.querySelectorAll('.recently-viewed__card');
      cards.forEach((card, i) => {
        setTimeout(() => card.classList.add('recently-viewed__card--loaded'), i * 50);
      });
    });
  }

  buildCard(product, index) {
    const featuredImage = product.featured_image
      ? this.buildImageUrl(product.featured_image, 533)
      : '';
    const secondImage =
      product.images && product.images.length > 1
        ? this.buildImageUrl(product.images[1], 533)
        : '';

    const price = this.formatMoney(product.price);
    const compareAtPrice =
      this.showCompareAtPrice && product.compare_at_price && product.compare_at_price > product.price
        ? this.formatMoney(product.compare_at_price)
        : '';
    const available = product.available;
    const onSale = compareAtPrice !== '';
    const soldOut = !available;

    let badgeHtml = '';
    if (this.showSaleBadge) {
      if (soldOut) {
        badgeHtml = `<span class="badge badge--bottom-left color-{{ settings.sold_out_badge_color_scheme | default: 'background-1' }}">Sold out</span>`;
      } else if (onSale) {
        badgeHtml = `<span class="badge badge--bottom-left color-{{ settings.sale_badge_color_scheme | default: 'background-1' }}">Sale</span>`;
      }
    }

    const imgAttrs = featuredImage
      ? `src="${featuredImage}" alt="${this.escapeHtml(product.title)}" width="533" height="${Math.round(533 / (product.image_aspect_ratio || 1))}" loading="lazy"`
      : '';

    const secondImgAttrs = secondImage
      ? `src="${secondImage}" alt="${this.escapeHtml(product.title)}" width="533" height="${Math.round(533 / (product.image_aspect_ratio || 1))}" loading="lazy"`
      : '';

    const vendorHtml = this.showVendor
      ? `<div class="card-information__vendor caption-with-letter-spacing light">${this.escapeHtml(product.vendor)}</div>`
      : '';

    const ratingHtml = this.showRating && product.metafields && product.metafields.reviews
      ? this.buildRatingHtml(product.metafields.reviews)
      : '';

    const quickAddHtml = this.showQuickAdd && available
      ? `<quick-add-modal>
          <button
            class="quick-add__submit button button--full-width button--secondary"
            data-product-url="${this.escapeHtml(product.url)}"
            aria-label="Add ${this.escapeHtml(product.title)} to cart"
          >
            <span class="quick-add__submit-text">Add to cart</span>
            <span class="loading__spinner hidden">
              <svg aria-hidden="true" focusable="false" role="presentation" class="spinner" viewBox="0 0 66 66"><circle class="path" fill="none" stroke-width="6" cx="33" cy="33" r="30"></circle></svg>
            </span>
          </button>
        </quick-add-modal>`
      : '';

    return `
      <div class="grid__item recently-viewed__card" data-product-handle="${this.escapeHtml(product.handle)}">
        <div class="card-wrapper product-card-wrapper underline-links-hover">
          <div class="card card--standard card--media">
            <div class="card__inner ratio" style="--ratio-percent: 100%;">
              <div class="card__media">
                <div class="media media--transparent media--hover-effect">
                  ${featuredImage ? `<img class="motion-reduce" ${imgAttrs}>` : `<div class="placeholder-svg">${this.placeholderSvg()}</div>`}
                  ${secondImage ? `<img class="motion-reduce" loading="lazy" ${secondImgAttrs}>` : ''}
                </div>
              </div>
              <div class="card__badge card__badge--custom">
                ${badgeHtml}
              </div>
              <button
                class="recently-viewed__remove"
                data-remove-handle="${this.escapeHtml(product.handle)}"
                aria-label="Remove ${this.escapeHtml(product.title)} from recently viewed"
              >
                <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><line x1="18" y1="6" x2="6" y2="18"></line><line x1="6" y1="6" x2="18" y2="18"></line></svg>
              </button>
            </div>
            <div class="card__content">
              <div class="card__information">
                <h3 class="card__heading h5">
                  <a href="${this.escapeHtml(product.url)}" class="full-unstyled-link">
                    ${this.escapeHtml(product.title)}
                  </a>
                </h3>
                <div class="card-information">
                  ${vendorHtml}
                  ${ratingHtml}
                  ${this.showPrice ? `
                  <div class="price">
                    <div class="price__container">
                      ${onSale ? `
                        <span class="price-item price-item--sale">
                          ${price}
                        </span>
                        <span class="price-item price-item--regular">
                          <s>${compareAtPrice}</s>
                        </span>
                      ` : `
                        <span class="price-item price-item--regular">
                          ${price}
                        </span>
                      `}
                    </div>
                  </div>` : ''}
                  ${quickAddHtml}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    `;
  }

  buildRatingHtml(reviews) {
    if (!reviews.rating || !reviews.rating.value) return '';
    const rating = parseFloat(reviews.rating.value.rating) || 0;
    const count = reviews.rating.count || 0;
    if (rating === 0) return '';

    let decimal = rating % 1;
    let fullStars = Math.floor(rating);
    let halfStar = 0;
    if (decimal >= 0.3 && decimal <= 0.7) {
      halfStar = 1;
    } else if (decimal > 0.7) {
      fullStars += 1;
    }

    let starsHtml = '';
    for (let i = 0; i < fullStars; i++) {
      starsHtml += '<svg class="icon icon-star" viewBox="0 0 24 24"><path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z"/></svg>';
    }
    if (halfStar) {
      starsHtml += '<svg class="icon icon-star-half" viewBox="0 0 24 24"><path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77V2z"/></svg>';
    }

    return `
      <div class="rating" role="img" aria-label="${rating.toFixed(1)} out of 5 stars from ${count} reviews">
        <div class="rating__stars">${starsHtml}</div>
        <span class="rating__caption visually-hidden">${rating.toFixed(1)} out of 5 stars</span>
      </div>
    `;
  }

  initQuickAdd() {
    if (!this.showQuickAdd) return;

    this.querySelectorAll('.quick-add__submit').forEach((button) => {
      button.addEventListener('click', (e) => {
        e.preventDefault();
        const productUrl = button.getAttribute('data-product-url');
        if (!productUrl) return;

        this.fetchAndShowQuickAdd(productUrl, button);
      });
    });

    this.querySelectorAll('.recently-viewed__remove').forEach((button) => {
      button.addEventListener('click', (e) => {
        e.preventDefault();
        e.stopPropagation();
        const handle = button.getAttribute('data-remove-handle');
        this.removeProduct(handle);
      });
    });
  }

  async fetchAndShowQuickAdd(productUrl, button) {
    button.setAttribute('aria-disabled', true);
    button.classList.add('loading');
    const spinner = button.querySelector('.loading__spinner');
    if (spinner) spinner.classList.remove('hidden');

    try {
      const response = await fetch(productUrl);
      const html = await response.text();
      const doc = new DOMParser().parseFromString(html, 'text/html');
      const productInfo = doc.querySelector('product-info');

      if (!productInfo) return;

      const form = productInfo.querySelector('product-form');
      if (!form) return;

      const addToCartForm = form.querySelector('form');
      if (!addToCartForm) return;

      const formData = new FormData(addToCartForm);
      const sectionId = addToCartForm.querySelector('input[name="section_id"]');

      const cartResponse = await fetch('/cart/add.js', {
        method: 'POST',
        body: formData,
      });

      if (cartResponse.ok) {
        const cartItem = await cartResponse.json();

        const cartNotification = document.querySelector('cart-notification') || document.querySelector('cart-drawer');
        if (cartNotification) {
          cartNotification.renderContents(cartItem);
        }

        button.removeAttribute('aria-disabled');
        button.classList.remove('loading');
        if (spinner) spinner.classList.add('hidden');
      } else {
        throw new Error('Add to cart failed');
      }
    } catch (error) {
      window.location.href = productUrl;
    }
  }

  removeProduct(handle) {
    const card = this.querySelector(`[data-product-handle="${handle}"]`);
    if (card) {
      card.style.transition = 'opacity 0.3s ease, transform 0.3s ease';
      card.style.opacity = '0';
      card.style.transform = 'scale(0.9)';
      setTimeout(() => {
        card.remove();
        this.removeFromStored(handle);

        const remainingCards = this.querySelectorAll('.recently-viewed__card');
        if (remainingCards.length === 0) {
          this.showEmpty();
        }
      }, 300);
    }
  }

  buildImageUrl(image, width) {
    if (!image) return '';
    const url = typeof image === 'string' ? image : image.src;
    const cleanUrl = url.split('?')[0];
    return `${cleanUrl}?width=${width}`;
  }

  formatMoney(cents) {
    if (typeof Shopify !== 'undefined' && Shopify.formatMoney) {
      return Shopify.formatMoney(cents, Shopify.money_format || '${{amount}}');
    }
    const amount = (cents / 100).toFixed(2);
    const currency = (typeof Shopify !== 'undefined' && Shopify.currency && Shopify.currency.active) || '£';
    return `${currency}${amount}`;
  }

  escapeHtml(str) {
    if (!str) return '';
    const div = document.createElement('div');
    div.textContent = str;
    return div.innerHTML;
  }

  placeholderSvg() {
    return `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 525 525"><rect fill="#cccccc" width="533" height="533"/><text fill="#999999" font-family="Arial,sans-serif" font-size="26" x="50%" y="50%" dominant-baseline="middle" text-anchor="middle">No Image</text></svg>`;
  }
}

if (!customElements.get('recently-viewed-products')) {
  customElements.define('recently-viewed-products', RecentlyViewedProducts);
}
